import { queryOptions } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { ReportJobStatus } from "../../../shared/interfaces/JobStatus";
import { getJobStatus, type JobStatusSnapshot } from "./jobStatusApi";

export const jobKeys = {
  all: ["jobs"] as const,
  statuses: () => [...jobKeys.all, "status"] as const,
  status: (jobId: string) => [...jobKeys.statuses(), jobId] as const,
};

export const JOB_STATUS_POLL_MS = 3000;

/**
 * Pure poll predicate (the templateJobPollInterval pattern).
 *
 * Stops on AWAITING_INPUT (paused for human input — the fan-out's
 * focus/visibility checks detect resumption elsewhere), terminal statuses,
 * and hard HTTP errors (the fan-out marks those FAILED).
 * Keeps polling through network blips — unlike the old setInterval loop,
 * which failed the job on any error.
 */
export function jobStatusPollInterval(
  data: JobStatusSnapshot | undefined,
  error: unknown,
): number | false {
  if (error) {
    return isAxiosError(error) && error.response ? false : JOB_STATUS_POLL_MS;
  }
  if (
    data?.jobStatus === ReportJobStatus.AWAITING_INPUT ||
    data?.jobStatus === ReportJobStatus.PENDING_REVIEW ||
    data?.jobStatus === ReportJobStatus.FAILED
  ) {
    return false;
  }
  return JOB_STATUS_POLL_MS;
}

export function jobStatusQuery(jobId: string) {
  return queryOptions<JobStatusSnapshot>({
    queryKey: jobKeys.status(jobId),
    queryFn: ({ signal }) => getJobStatus(jobId, signal),
    // The predicate owns ride-through-blips; query retries would only delay
    // hard-failure feedback.
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (query) =>
      jobStatusPollInterval(query.state.data, query.state.error),
    // The old setInterval ignored tab visibility.
    refetchIntervalInBackground: true,
    // Covers the tab-hidden→visible case (v5's focusManager listens to
    // visibilitychange only). OS window refocus with the tab visible is
    // handled by useActiveJobStatusSync's window-focus listener.
    refetchOnWindowFocus: true,
  });
}
